import { JsonProperty, Serializable } from 'ts-jackson'
import { Fromable } from '../model'

export interface TrainerScheduleParam {
  duration: number
  startTime: string
  endTime?: string
  // reference instant for past-slot filtering when listing slots for
  // a retroactive (past-date) PT booking.
  asOf?: string
}

export interface TrainerScheduleBody {
  memberId: number
  trainerId: number
  userMembershipId?: number
  serviceId: number
  startTime: string
  endTime: string
}

export interface TrainerServiceBody {
  groupId: number
  nameEn: string
  nameTh: string
  description?: string
}

export interface ServiceGroupSubCategory {
  id: number
  nameEn: string
  nameTh: string
  inUse: boolean
}

export interface ServiceGroupDetail {
  id: number
  nameEn: string
  nameTh: string
  services: ServiceGroupSubCategory[]
}

export interface ServiceGroupListResponse {
  data: ServiceGroupDetail[]
  pagination: { total: number; page: number; pageSize: number }
}

export interface CreateServiceGroupBody {
  nameEn: string
  nameTh: string
  subCategories: { nameEn: string; nameTh: string }[]
}

export interface UpdateSubCategoryItem {
  id?: number
  nameEn: string
  nameTh: string
  action: 'add' | 'edit' | 'delete' | 'none'
}

export interface UpdateServiceGroupBody {
  nameEn: string
  nameTh: string
  subCategories: UpdateSubCategoryItem[]
}

@Serializable()
export class TrainerSchedule extends Fromable {
  @JsonProperty('startTime')
  startTime!: string

  @JsonProperty('endTime')
  endTime!: string

  public serialize() {
    return this
  }
}

@Serializable()
export class TrainerScheduleBooking extends Fromable {
  @JsonProperty('id')
  id!: number

  @JsonProperty('memberId')
  memberId!: number

  @JsonProperty('trainerId')
  trainerId!: number

  @JsonProperty('branchId')
  branchId!: number

  @JsonProperty('userMembershipId')
  userMembershipId!: number

  @JsonProperty('startTime')
  startTime!: string

  @JsonProperty('endTime')
  endTime!: string

  @JsonProperty('bookedAt')
  bookedAt?: string | null

  @JsonProperty('cancelledAt')
  cancelledAt?: string | null

  @JsonProperty('status')
  status!: string

  @JsonProperty('member')
  member!: UserLite

  @JsonProperty('trainer')
  trainer!: UserLite

  @JsonProperty('userMembership')
  userMembership!: UserMembership

  @JsonProperty('service')
  service!: Service

  public serialize() {
    return this
  }
}

@Serializable()
export class UserLite extends Fromable {
  @JsonProperty('id')
  id!: number

  @JsonProperty('imageUrl')
  imageUrl!: string

  @JsonProperty('code')
  code!: string

  @JsonProperty('firstName')
  firstName!: string

  @JsonProperty('lastName')
  lastName!: string

  @JsonProperty('nickname')
  nickname!: string

  @JsonProperty('gender')
  gender!: string

  @JsonProperty('email')
  email!: string

  @JsonProperty('phone')
  phone!: string

  public serialize() {
    return this
  }
}

@Serializable()
export class UserMembership extends Fromable {
  @JsonProperty('id')
  id!: number

  @JsonProperty('startDate')
  startDate!: string

  @JsonProperty('expireDate')
  expireDate?: string | null

  @JsonProperty('remainingUses')
  remainingUses!: number

  @JsonProperty('isActive')
  isActive!: boolean

  @JsonProperty('branchId')
  branchId?: number | null

  @JsonProperty('createdAt')
  createdAt!: string

  @JsonProperty('user')
  user!: UserLite

  @JsonProperty('owner')
  owner!: UserLite

  @JsonProperty('sale')
  sale!: UserLite

  public serialize() {
    return this
  }
}

@Serializable()
export class Service extends Fromable {
  @JsonProperty('id')
  id!: number

  @JsonProperty('groupId')
  groupId?: number | null

  @JsonProperty('nameEn')
  nameEn!: string

  @JsonProperty('nameTh')
  nameTh!: string

  @JsonProperty('description')
  description?: string | null

  @JsonProperty('createdAt')
  createdAt!: string

  public serialize() {
    return this
  }
}

@Serializable()
export class CreditBalance extends Fromable {
  @JsonProperty('userId')
  userId!: number

  @JsonProperty('totalCredits')
  totalCredits!: number

  @JsonProperty('updatedAt')
  updatedAt!: string

  public serialize() {
    return this
  }
}

/**
 * Which kind of training the client list counts as "this trainer trained this
 * member" (QTY-2082). A class booking does not make someone a PT client, so the
 * PT page always sends one — `pt`, or `swimming` for an aquatic-only coach (see
 * `resolveStaffActivityType`) — and the swimming coach page sends `swimming`.
 * The endpoint still answers an omitted param with every activity merged, which
 * the trainer mobile app relies on; the CRM never asks for that.
 *
 * `CLASS` has no caller on this side: it mirrors the API's enum
 * (nongcalcal/nova-ignite-backend#902), which accepts and answers all three, so
 * the union stays the endpoint's contract rather than the subset we happen to
 * send today.
 */
export const TRAINING_HISTORY_ACTIVITY_TYPE = {
  PT: 'pt',
  CLASS: 'class',
  SWIMMING: 'swimming',
} as const

export type TrainingHistoryActivityType =
  (typeof TRAINING_HISTORY_ACTIVITY_TYPE)[keyof typeof TRAINING_HISTORY_ACTIVITY_TYPE]

@Serializable()
export class ClientHistoryData extends Fromable {
  @JsonProperty('id')
  id!: number

  @JsonProperty('imageUrl')
  imageUrl!: string

  @JsonProperty('code')
  code!: string

  @JsonProperty('firstName')
  firstName!: string

  @JsonProperty('lastName')
  lastName!: string

  @JsonProperty('nickname')
  nickname!: string

  @JsonProperty('gender')
  gender!: string

  @JsonProperty('swimmingLastTrainedDate')
  swimmingLastTrainedDate?: string

  @JsonProperty('lastTrainedDate')
  lastTrainedDate?: string
}

@Serializable()
export class CoachTrainingHistory extends Fromable {
  @JsonProperty('id')
  id!: number
  @JsonProperty('userId')
  userId!: number

  @JsonProperty('scheduleId')
  scheduleId!: number

  @JsonProperty('status')
  status!: string

  @JsonProperty('bookedAt')
  bookedAt!: string

  @JsonProperty('cancelledAt')
  cancelledAt!: string

  @JsonProperty({ path: 'schedule', type: TrainerSchedule })
  schedule!: TrainerSchedule

  @JsonProperty('user')
  user!: CoachUserData

  @JsonProperty('userMembership')
  userMembership!: UserMembership[]

  public DataForTable() {
    return {
      id: this.id,
      dateTime: this.bookedAt,
      status: this.status,
      name: this.user.firstName + ' ' + this.user.lastName,
      phone: this.user.phone,
    }
  }
}

@Serializable()
export class CoachUserData extends Fromable {
  @JsonProperty('id')
  id!: number
  @JsonProperty('imageUrl')
  imageUrl!: string
  @JsonProperty('code')
  code!: string
  @JsonProperty('firstName')
  firstName!: string
  @JsonProperty('lastName')
  lastName!: string
  @JsonProperty('nickname')
  nickname!: string
  @JsonProperty('gender')
  gender!: string
  @JsonProperty('email')
  email!: string
  @JsonProperty('phone')
  phone!: string
  @JsonProperty('userMembership')
  userMembership!: CoachMembership[]
}

@Serializable()
export class CoachMembership extends Fromable {
  @JsonProperty('id')
  id!: number
  @JsonProperty('userId')
  userId!: number
  @JsonProperty('packageId')
  packageId!: number
  @JsonProperty('startDate')
  startDate!: string
  @JsonProperty('expireDate')
  expireDate!: string
  @JsonProperty('remainingUses')
  remainingUses!: number
  @JsonProperty('joiningFee')
  joiningFee!: number
  @JsonProperty('isActive')
  isActive!: boolean
  @JsonProperty('branchId')
  branchId!: number
  @JsonProperty('createdAt')
  createdAt!: string
  @JsonProperty('note')
  note!: string
  @JsonProperty('ownerId')
  ownerId!: number
  @JsonProperty('saleId')
  saleId!: number
}
